/**
 * Signup Screen
 * Handles user registration with email/password
 * Redesigned with pill-shaped inputs to match auth-screens-preview
 *
 * AUTH-013: two-state screen. When registration requires email confirmation,
 * the form switches in place to a "confirm your email" state with a 6-digit
 * code input — the emailed code is the primary path; the emailed link stays
 * as fallback. Verify success signs the user straight in (SIGNUP_VERIFIED →
 * authenticated), so there is no manual navigation on success.
 */

import React, { useState, useMemo } from 'react';
import { Platform, ScrollView, View, StyleSheet, Linking } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import {
  Box,
  VStack,
  HStack,
  Text,
  Pressable,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { useAuthStore } from '../../store/authStore';
import { E2E_MODE } from '../../config/constants';
import { getPrivacyPolicyUrl, getTermsOfServiceUrl } from '../../config/legalUrls';
import {
  OTP_CODE_LENGTH,
  isValidOtpCode,
  sanitizeOtpCodeInput,
} from '../../auth/otpCode';
import { AUTH_RESEND_COOLDOWN_MS } from '../../config/timeouts';
import { useResendCooldown } from '../../hooks/useResendCooldown';
import { PillInput } from '../../components/auth/PillInput';
import { PillButton } from '../../components/auth/PillButton';
import { PasswordStrength } from '../../components/auth/PasswordStrength';
import { TextLinkButton } from '../../components/ui';
import { useAppBannerStore } from '../../hooks/useAppBanner';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FS_TITLE = 28;
const FS_CONFIRM_TITLE = 24;
const FS_CHECKMARK = 14;
const FS_ENVELOPE = 40;

// Notification state for web
export type Notification = { title: string; message: string; type: 'error' | 'success' } | null;

export interface SignupScreenDisplayProps {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  isSubmitting: boolean;
  notification: Notification;
  touched: { name: boolean; email: boolean; password: boolean; confirmPassword: boolean };
  validation: { name: boolean; email: boolean; password: boolean; confirmPassword: boolean };
  isFormValid: boolean;
  /** Non-null → the "confirm your email" state is shown for this address */
  confirmingEmail: string | null;
  code: string;
  codeTouched: boolean;
  isCodeValid: boolean;
  isVerifying: boolean;
  verifyError: string | null;
  resendSecondsLeft: number;
  isResending: boolean;
  onNameChange: (text: string) => void;
  onEmailChange: (text: string) => void;
  onPasswordChange: (text: string) => void;
  onConfirmPasswordChange: (text: string) => void;
  onToggleTerms: () => void;
  onBlur: (field: 'name' | 'email' | 'password' | 'confirmPassword') => void;
  onSignup: () => void;
  onDismissNotification: () => void;
  onNavigateLogin: () => void;
  onCodeChange: (text: string) => void;
  onVerifyCode: () => void;
  onResendCode: () => void;
  isE2EMode?: boolean;
}

export function SignupScreenDisplay({
  name,
  email,
  password,
  confirmPassword,
  acceptedTerms,
  isSubmitting,
  notification,
  touched,
  validation,
  isFormValid,
  confirmingEmail,
  code,
  codeTouched,
  isCodeValid,
  isVerifying,
  verifyError,
  resendSecondsLeft,
  isResending,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onToggleTerms,
  onBlur,
  onSignup,
  onDismissNotification,
  onNavigateLogin,
  onCodeChange,
  onVerifyCode,
  onResendCode,
  isE2EMode,
}: SignupScreenDisplayProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const checkboxBorder = theme.border;
  const checkboxBg = acceptedTerms ? colors.primary : 'transparent';
  const scaledCheckbox = Math.round(20 * fontScale);
  const scaledCircle = Math.round(80 * fontScale);

  // "Confirm your email" state — code entry (primary) + emailed link (fallback)
  if (confirmingEmail) {
    const resendDisabled = resendSecondsLeft > 0 || isResending;

    return (
      <KeyboardAvoidingView
        style={{ flex: 1, backgroundColor: theme.background }}
      >
        <ScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
        >
          <Box
            flex={1}
            justifyContent="center"
            alignItems="center"
            px="$6"
            py="$8"
            style={{ backgroundColor: theme.background }}
          >
            <VStack space="md" alignItems="center" w="$full" maxWidth={400}>
              {/* Envelope circle */}
              <View
                style={{
                  width: scaledCircle,
                  height: scaledCircle,
                  borderRadius: scaledCircle / 2,
                  backgroundColor: colors.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24,
                  shadowColor: colors.primary,
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.3,
                  shadowRadius: 16,
                  elevation: 8,
                }}
              >
                <Text allowFontScaling={false} style={{ color: 'white', fontSize: FS_ENVELOPE * fontScale, ...ffs }}>{'\u2709'}</Text>
              </View>

              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_CONFIRM_TITLE * fontScale,
                  fontWeight: '700',
                  color: colors.primary,
                  marginBottom: 8,
                  ...ffs
                }}
              >
                {t('auth.signup.confirmTitle')}
              </Text>

              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, textAlign: 'center', lineHeight: 24 * fontScale, ...ffs }}
              >
                {t('auth.signup.confirmSentTo')}
              </Text>

              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_MD * fontScale,
                  color: theme.text,
                  fontWeight: '600',
                  textAlign: 'center',
                  marginTop: 8,
                  marginBottom: 8,
                  ...ffs
                }}
              >
                {confirmingEmail}
              </Text>

              {/* Code entry (AUTH-013) */}
              <Box w="$full">
                <PillInput
                  label={t('auth.signup.codeLabel')}
                  icon={'\u{1F522}'}
                  testID="signup-input-code"
                  errorTestID="signup-text-code-invalid"
                  accessibilityLabel={t('auth.signup.codeAccessibility')}
                  placeholder="123456"
                  value={code}
                  onChangeText={onCodeChange}
                  keyboardType="number-pad"
                  textContentType="oneTimeCode"
                  autoComplete="one-time-code"
                  maxLength={OTP_CODE_LENGTH}
                  editable={!isVerifying}
                  isInvalid={codeTouched && !isCodeValid}
                  errorText={t('auth.signup.codeError')}
                />

                <PillButton
                  testID="signup-button-verify"
                  title={t('auth.signup.verifyCodeButton')}
                  onPress={onVerifyCode}
                  isLoading={isVerifying}
                  isDisabled={isVerifying || (codeTouched && !isCodeValid)}
                />

                {/* Verify error (invalid/expired code, rate limit, network) */}
                {verifyError && (
                  <Text
                    testID="signup-text-code-error"
                    accessibilityLiveRegion="polite"
                    allowFontScaling={false}
                    style={{ fontSize: FS_SM * fontScale, color: colors.error, textAlign: 'center', marginTop: 8, ...ffs }}
                  >
                    {verifyError}
                  </Text>
                )}

                <HStack justifyContent="center" mt="$3">
                  <TextLinkButton
                    testID="signup-button-resend"
                    accessibilityLabel={t('auth.signup.resendAccessibility')}
                    label={
                      resendSecondsLeft > 0
                        ? t('auth.signup.resendCountdown', { seconds: resendSecondsLeft })
                        : t('auth.signup.resendButton')
                    }
                    onPress={onResendCode}
                    disabled={resendDisabled}
                  />
                </HStack>
              </Box>

              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  color: theme.textSecondary,
                  textAlign: 'center',
                  lineHeight: 22 * fontScale,
                  marginBottom: 8,
                  ...ffs
                }}
              >
                {t('auth.signup.confirmExpiry')}
              </Text>

              <HStack space="sm" alignItems="center" mb="$6">
                <Text allowFontScaling={false} style={{ fontSize: 16 * fontScale, ...ffs }}>{'\u{1F4A1}'}</Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: colors.warning, ...ffs }}>
                  {t('auth.signup.confirmTip')}
                </Text>
              </HStack>

              <PillButton
                testID="signup-button-back-login"
                title={t('auth.signup.backToLogin')}
                onPress={onNavigateLogin}
                variant="secondary"
              />
            </VStack>
          </Box>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: theme.background }}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
      >
        <Box flex={1} justifyContent="center" alignItems="center" px="$6" py="$10">
          {/* Web notification banner */}
          {Platform.OS === 'web' && notification && (
            <Pressable
              testID="signup-button-dismiss"
              accessibilityLabel={t('common.dismiss')}
              accessibilityRole="button"
              onPress={onDismissNotification}
              style={{ position: 'absolute', top: 20, left: 20, right: 20, zIndex: 100 }}
            >
              <Box
                p="$4"
                borderRadius="$lg"
                bg={notification.type === 'error' ? colors.error : colors.success}
              >
                <Text allowFontScaling={false} style={{ color: 'white', fontWeight: 'bold', ...ffs }} mb="$1">{notification.title}</Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>{notification.message}</Text>
              </Box>
            </Pressable>
          )}

          {/* Heading */}
          <VStack space="sm" mb="$8" alignItems="flex-start" w="$full" maxWidth={400}>
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_TITLE * fontScale,
                fontWeight: '700',
                color: theme.text,
                marginTop: 24,
                ...ffs
              }}
            >
              {t('auth.signup.title')}
            </Text>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, lineHeight: 22 * fontScale, ...ffs }}
            >
              {t('auth.signup.subtitle')}
            </Text>
          </VStack>

          {/* Form */}
          <Box w="$full" maxWidth={400}>
            <PillInput
              label={t('auth.signup.fullNameLabel')}
              icon={'\u{1F464}'}
              testID="signup-input-name"
              accessibilityLabel={t('auth.signup.fullNameAccessibility')}
              placeholder={t('auth.signup.fullNamePlaceholder')}
              value={name}
              onChangeText={onNameChange}
              onBlur={() => onBlur('name')}
              autoCapitalize="words"
              maxLength={50}
              editable={!isSubmitting}
              isInvalid={touched.name && !validation.name}
              errorText={t('auth.signup.fullNameError')}
            />

            <PillInput
              label={t('auth.signup.emailLabel')}
              icon={'\u2709\uFE0F'}
              testID="signup-input-email"
              accessibilityLabel={t('auth.signup.emailAccessibility')}
              placeholder="you@example.com"
              value={email}
              onChangeText={onEmailChange}
              onBlur={() => onBlur('email')}
              autoCapitalize="none"
              keyboardType="email-address"
              editable={!isSubmitting}
              isInvalid={touched.email && !validation.email}
              errorText={t('auth.signup.emailError')}
            />

            <PillInput
              label={t('auth.signup.passwordLabel')}
              icon={'\u{1F512}'}
              isPassword
              forceShowPassword={isE2EMode}
              testID="signup-input-password"
              accessibilityLabel={t('auth.signup.passwordAccessibility')}
              placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
              value={password}
              onChangeText={onPasswordChange}
              onBlur={() => onBlur('password')}
              editable={!isSubmitting}
              textContentType={isE2EMode ? 'none' : 'newPassword'}
              autoComplete={isE2EMode ? 'off' : 'new-password'}
              isInvalid={touched.password && !validation.password}
              errorText={t('auth.signup.passwordError')}
            />
            <PasswordStrength password={password} />

            <View style={{ height: 8 }} />

            <PillInput
              label={t('auth.signup.confirmPasswordLabel')}
              icon={'\u{1F512}'}
              isPassword
              forceShowPassword={isE2EMode}
              testID="signup-input-confirm"
              accessibilityLabel={t('auth.signup.confirmPasswordAccessibility')}
              placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
              value={confirmPassword}
              onChangeText={onConfirmPasswordChange}
              onBlur={() => onBlur('confirmPassword')}
              editable={!isSubmitting}
              textContentType={isE2EMode ? 'none' : 'newPassword'}
              autoComplete={isE2EMode ? 'off' : 'new-password'}
              isInvalid={touched.confirmPassword && !validation.confirmPassword}
              errorText={t('auth.signup.confirmPasswordError')}
            />

            {/* Terms Checkbox */}
            <Pressable
              testID="signup-checkbox-terms"
              accessibilityLabel={acceptedTerms ? t('auth.signup.termsCheckboxUnchecked') : t('auth.signup.termsCheckboxChecked')}
              accessibilityRole="checkbox"
              onPress={onToggleTerms}
              disabled={isSubmitting}
              style={styles.checkboxRow}
            >
              <View
                style={{
                  width: scaledCheckbox,
                  height: scaledCheckbox,
                  borderRadius: Math.round(6 * fontScale),
                  borderWidth: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                  borderColor: acceptedTerms ? colors.primary : checkboxBorder,
                  backgroundColor: checkboxBg,
                }}
              >
                {acceptedTerms && (
                  <Text allowFontScaling={false} style={{ color: 'white', fontSize: FS_CHECKMARK * fontScale, fontWeight: 'bold', ...ffs }}>{'\u2713'}</Text>
                )}
              </View>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.text, flex: 1, lineHeight: 21 * fontScale, ...ffs }}
              >
                {t('auth.signup.termsAgreement')}{' '}
                <Text
                  allowFontScaling={false}
                  accessibilityRole="link"
                  testID="signup-link-terms"
                  onPress={() => Linking.openURL(getTermsOfServiceUrl())}
                  style={{ fontSize: FS_SM * fontScale, color: colors.primary, fontWeight: '500', textDecorationLine: 'underline', ...ffs }}
                >{t('auth.signup.termsOfService')}</Text>
                {' '}and{' '}
                <Text
                  allowFontScaling={false}
                  accessibilityRole="link"
                  testID="signup-link-privacy"
                  onPress={() => Linking.openURL(getPrivacyPolicyUrl())}
                  style={{ fontSize: FS_SM * fontScale, color: colors.primary, fontWeight: '500', textDecorationLine: 'underline', ...ffs }}
                >{t('auth.signup.privacyPolicy')}</Text>
              </Text>
            </Pressable>

            {/* Create Account Button */}
            <PillButton
              testID="signup-button-submit"
              title={t('auth.signup.createAccountButton')}
              onPress={onSignup}
              isLoading={isSubmitting}
              isDisabled={isSubmitting || !isFormValid}
            />

            {/* Login Link */}
            <HStack justifyContent="center" mt="$2" space="xs">
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('auth.signup.alreadyHaveAccount')}
              </Text>
              <TextLinkButton testID="signup-link-login" accessibilityLabel={t('auth.signup.loginAccessibility')} accessibilityRole="link" label={t('auth.signup.loginLink')} onPress={onNavigateLogin} disabled={isSubmitting} />
            </HStack>
          </Box>
        </Box>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

export function SignupScreen() {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const {
    register,
    verifySignupCode,
    resendSignupConfirmation,
    isSubmitting,
    emailConfirmationRequired,
  } = useAuthStore();
  const [notification, setNotification] = useState<Notification>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
    confirmPassword: false,
  });

  // AUTH-013 confirm-your-email state (non-null → code entry is shown)
  const [confirmingEmail, setConfirmingEmail] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const resendCooldown = useResendCooldown(AUTH_RESEND_COOLDOWN_MS);

  const showBanner = useAppBannerStore((s) => s.showBanner);

  // Show message via banner (works on all platforms)
  const showMessage = (
    title: string,
    message: string,
    type: 'error' | 'success' = 'error',
  ) => {
    if (Platform.OS === 'web') {
      setNotification({ title, message, type });
      setTimeout(() => setNotification(null), 5000);
    } else {
      showBanner({ message, type });
    }
  };

  // Validation
  const validation = useMemo(() => {
    return {
      name: name.trim().length >= 2,
      email: EMAIL_REGEX.test(email),
      password: password.length >= 8,
      confirmPassword: password === confirmPassword && confirmPassword.length > 0,
    };
  }, [name, email, password, confirmPassword]);

  const isFormValid =
    validation.name &&
    validation.email &&
    validation.password &&
    validation.confirmPassword &&
    acceptedTerms;

  const isCodeValid = isValidOtpCode(code);

  // Enter the confirm-your-email state when `emailConfirmationRequired`
  // transitions false→true while this screen is mounted — that's register()
  // resolving without a session (auto-confirm environments never flip the
  // flag: isAuthenticated flips instead and AppNavigator switches groups).
  // Watching the store flag rather than the register() call keeps a single
  // entry seam that the E2E dev intent (`set-email-confirmation-required`)
  // can also drive. The transition guard means a stale flag left by an
  // abandoned registration can't re-trigger on mount.
  const prevConfirmationFlag = React.useRef(emailConfirmationRequired);
  React.useEffect(() => {
    const wasRequired = prevConfirmationFlag.current;
    prevConfirmationFlag.current = emailConfirmationRequired;
    if (emailConfirmationRequired && !wasRequired && email && !confirmingEmail) {
      setConfirmingEmail(email);
      // The signup itself sent the confirmation email, opening Supabase's
      // max_frequency window.
      resendCooldown.start();
    }
  }, [emailConfirmationRequired, email, confirmingEmail, resendCooldown]);

  const handleSignup = async () => {
    setTouched({ name: true, email: true, password: true, confirmPassword: true });

    if (!isFormValid) {
      if (!acceptedTerms) {
        showMessage(t('auth.signup.termsRequiredTitle'), t('auth.signup.termsRequiredMessage'));
      }
      return;
    }

    try {
      // Success needs no handling here: either the flag effect above enters
      // the confirm state, or (auto-confirm) isAuthenticated flips and
      // AppNavigator switches groups. This replaces the old success banner
      // + goBack().
      await register(email, password, name.trim());
    } catch (_error: unknown) {
      const errorMessage = useAuthStore.getState().error || 'Registration failed';
      showMessage(t('auth.signup.registrationFailedTitle'), errorMessage);
    }
  };

  const handleCodeChange = (text: string) => {
    setCode(sanitizeOtpCodeInput(text));
    if (verifyError) setVerifyError(null);
  };

  const handleVerifyCode = async () => {
    setCodeTouched(true);

    // Client-side guard: no provider call for a malformed code
    if (!isValidOtpCode(code) || !confirmingEmail) {
      return;
    }

    setIsVerifying(true);
    setVerifyError(null);
    try {
      // Signs the user in on success (SIGNUP_VERIFIED → authenticated):
      // isAuthenticated flips and AppNavigator switches groups — no manual
      // navigation here.
      await verifySignupCode(confirmingEmail, code);
    } catch (err: unknown) {
      setVerifyError((err as Error).message);
      // Clear for retry — a consumed/expired code can't succeed on re-submit
      setCode('');
      setCodeTouched(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown.isCoolingDown || isResending || !confirmingEmail) return;
    setIsResending(true);
    try {
      await resendSignupConfirmation(confirmingEmail);
      setVerifyError(null);
      resendCooldown.start();
    } catch (err: unknown) {
      showBanner({ message: (err as Error).message });
    } finally {
      setIsResending(false);
    }
  };

  const handleBlur = (field: keyof typeof touched) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // E2E mode
  const isE2EMode = E2E_MODE;

  return (
    <SignupScreenDisplay
      name={name}
      email={email}
      password={password}
      confirmPassword={confirmPassword}
      acceptedTerms={acceptedTerms}
      isSubmitting={isSubmitting}
      notification={notification}
      touched={touched}
      validation={validation}
      isFormValid={isFormValid}
      confirmingEmail={confirmingEmail}
      code={code}
      codeTouched={codeTouched}
      isCodeValid={isCodeValid}
      isVerifying={isVerifying}
      verifyError={verifyError}
      resendSecondsLeft={resendCooldown.secondsLeft}
      isResending={isResending}
      onNameChange={setName}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onConfirmPasswordChange={setConfirmPassword}
      onToggleTerms={() => setAcceptedTerms(!acceptedTerms)}
      onBlur={handleBlur}
      onSignup={handleSignup}
      onDismissNotification={() => setNotification(null)}
      onNavigateLogin={() => navigation.goBack()}
      onCodeChange={handleCodeChange}
      onVerifyCode={handleVerifyCode}
      onResendCode={handleResendCode}
      isE2EMode={isE2EMode}
    />
  );
}

const styles = StyleSheet.create({
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 24,
  },
});
